import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  associateWebAuthnCredential,
  listWebAuthnCredentials,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { normalizeEmail } from './normalize-email.js';

export type AuthPage = 'auth' | 'dashboard';

export interface AuthUser {
  userId: string;
  email: string;
  profile: string | null;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string | null;
  currentPage: AuthPage;
  error: string | null;
  hasPasskey: boolean;

  // Auth actions
  initiateAuth: (email: string) => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  sendVerificationCode: () => Promise<void>;
  verifyCode: (code: string) => Promise<{ hasPasskey: boolean }>;
  registerPasskey: () => Promise<void>;
  updateProfile: (profile: string) => Promise<void>;
  skipPasskey: () => void;
  signOut: () => Promise<void>;
  clearError: () => void;

  // Navigation
  navigateTo: (page: AuthPage) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<AuthPage>('auth');
  const [error, setError] = useState<string | null>(null);
  const [hasPasskey, setHasPasskey] = useState(false);

  useEffect(() => {
    void checkSession();
  }, []);

  async function checkSession(): Promise<void> {
    setIsLoading(true);
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const attributes = await fetchUserAttributes();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        throw new Error('No access token in session');
      }
      const loginId = currentUser.signInDetails?.loginId;
      if (!loginId) {
        throw new Error('No login email found in current session');
      }
      setUser({ userId: currentUser.userId, email: loginId, profile: attributes.profile ?? null });
      setEmail(loginId);
      setIsAuthenticated(true);
      setCurrentPage('dashboard');
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      setCurrentPage('auth');
    } finally {
      setIsLoading(false);
    }
  }

  async function applySession(authenticatedEmail: string): Promise<void> {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    const attributes = await fetchUserAttributes();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('No access token in session after sign-in');
    }
    setUser({ userId: currentUser.userId, email: authenticatedEmail, profile: attributes.profile ?? null });
    setEmail(authenticatedEmail);
    setIsAuthenticated(true);
    setCurrentPage('dashboard');
  }

  const initiateAuth = useCallback(async (email: string): Promise<void> => {
    const normalized = normalizeEmail(email);
    setEmail(normalized);
    setError(null);
    try {
      await amplifySignIn({
        username: normalized,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      });
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === 'UserNotFoundException' || err.message.includes('User does not exist'))) {
        const remapped = new Error('Unable to sign in. Please check your email and try again.');
        setError(remapped.message);
        throw remapped;
      }
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    }
  }, []);

  const signInWithPasskey = useCallback(async (): Promise<void> => {
    if (!email) {
      throw new Error('No email set — call initiateAuth first');
    }
    const currentEmail = normalizeEmail(email);
    try {
      setError(null);
      const result = await amplifySignIn({
        username: currentEmail,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'WEB_AUTHN' },
      });
      if (!result.isSignedIn) {
        throw new Error(`Unexpected sign-in step after passkey: ${result.nextStep?.signInStep}`);
      }
      await applySession(currentEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed');
      throw err;
    }
  }, [email]);

  const sendVerificationCode = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      await confirmSignIn({ challengeResponse: 'EMAIL_OTP' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      throw err;
    }
  }, []);

  const verifyCode = useCallback(async (code: string): Promise<{ hasPasskey: boolean }> => {
    if (!email) {
      throw new Error('No email set — call initiateAuth first');
    }
    const currentEmail = normalizeEmail(email);
    try {
      setError(null);
      const result = await confirmSignIn({ challengeResponse: code });
      if (!result.isSignedIn) {
        throw new Error(`Unexpected sign-in step after code: ${result.nextStep?.signInStep}`);
      }
      const credentials = await listWebAuthnCredentials();
      const userHasPasskey = credentials.credentials.length > 0;
      setHasPasskey(userHasPasskey);
      await applySession(currentEmail);
      return { hasPasskey: userHasPasskey };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code verification failed');
      throw err;
    }
  }, [email]);

  const registerPasskey = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      await associateWebAuthnCredential();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey registration failed');
      throw err;
    }
  }, []);

  const updateProfile = useCallback(async (profile: string): Promise<void> => {
    if (!user) {
      throw new Error('No authenticated user available for profile update');
    }

    setError(null);
    try {
      const result = await updateUserAttributes({ userAttributes: { profile } });
      const updateStep = result.profile?.nextStep.updateAttributeStep;
      if (updateStep !== 'DONE') {
        throw new Error(`Cognito did not complete the profile update: ${updateStep ?? 'missing update state'}`);
      }
      setUser((currentUser) => currentUser ? { ...currentUser, profile } : currentUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Profile update failed');
      throw err;
    }
  }, [user]);

  const skipPasskey = useCallback((): void => {
    setCurrentPage('dashboard');
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await amplifySignOut();
    setUser(null);
    setEmail(null);
    setIsAuthenticated(false);
    setCurrentPage('auth');
    setError(null);
    setHasPasskey(false);
  }, []);

  const navigateTo = useCallback((page: AuthPage) => {
    setCurrentPage(page);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      email,
      currentPage,
      error,
      hasPasskey,
      initiateAuth,
      signInWithPasskey,
      sendVerificationCode,
      verifyCode,
      registerPasskey,
      updateProfile,
      skipPasskey,
      signOut,
      clearError,
      navigateTo,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
